import React, { Fragment, useEffect, useState, useRef } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { ToastService } from '@iamsoftware/react-hooks';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ScrollPanel } from 'primereact/scrollpanel';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';

import { AgreementTemplateService as Service } from './AgreementTemplateService';
import { AgreementService } from 'src/service/AgreementService';
import { Oauth2GoogleService } from '../../hop-dong/mau-hop-dong/Oauth2GoogleService';
import { Step1BasicInfo } from './components/Step1BasicInfo';
import { Step2Roles } from './components/Step2Roles';
import { Step3Positions } from './components/Step3Positions';
import { Step4Fields } from './components/Step4Fields';
import { Toast } from 'primereact/toast';

GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs';

export const TemplateDetail = ({ display, setDisplay, documentTypeOptions = [] }: {
  display: any;
  setDisplay: (v: any) => void;
  documentTypeOptions?: any[];
}) => {

  const [activeIndex, setActiveIndex] = useState(0);
  const [templateData, setTemplateData] = useState<any>(null);
  const [formFieldTypes, setFormFieldTypes] = useState<any[]>([]);
  const [fileLoading, setFileLoading] = useState(false);
  const [roles, setRoles] = useState<any[]>([]);
  const [signaturePositions, setSignaturePositions] = useState<any[]>([]);
  const [dbFormFields, setDbFormFields] = useState<any[]>([]);
  const [mapProperties, setMapProperties] = useState<any[]>([]);
  const [canvases, setCanvases] = useState<HTMLCanvasElement[]>([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const canvasRefs = useRef<any[]>([]);
  const draggableRefs = useRef({} as any);
  const toast = useRef<Toast>(null);

  useEffect(() => {
    if (display?.agreementId) {
      Service.get(display.agreementId).then(data => {
        console.log('Template Data from BE:', data);
        setTemplateData(data);
        if (data.roles) {
          setRoles(data.roles.map((r: any) => ({
            roleTypeId: r.roleTypeId,
            roleTypeName: r.description || '',
            partyId: r.partyId
          })));
        }
        if (data.signaturePositions) {
          setSignaturePositions(data.signaturePositions.map((p: any) => ({
            ...p,
            pageIndex: p.pageNumber - 1,
            x: p.ulx,
            y: p.uly
          })));
        }
        if (data.dbFormFields) {
          setDbFormFields(data.dbFormFields.map((f: any) => ({
            ...f,
            isRequired: f.isRequired === 'Y'
          })));
        }
        if (data.mapProperties) {
          setMapProperties(data.mapProperties);
        }
      });
      Service.getFormFieldTypes().then(data => {
        setFormFieldTypes(data.listData.map((item: any) => ({
          value: item.enumId,
          label: item.description
        })));
      });
    } else {
      setTemplateData(null);
      setFileLoading(false);
      setRoles([]);
      setSignaturePositions([]);
      setDbFormFields([]);
      setMapProperties([]);
      setCanvases([]);
    }
  }, [display]);

  useEffect(() => {
    const fileIdToLoad = templateData?.referenceFileId || templateData?.fileId;
    if (display && fileIdToLoad) {
      loadPdf(fileIdToLoad);
    }
  }, [display, templateData?.referenceFileId, templateData?.fileId]);

  const loadPdf = async (fId: string) => {
    setPdfLoading(true);
    try {
      const blob = await AgreementService.downloadPdf(fId);
      const arrayBuffer = await blob.arrayBuffer();
      const loadingTask = getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      const promises = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        promises.push(renderPage(pdf, i));
      }
      const result = await Promise.all(promises);
      setCanvases(result);
    } catch (err) {
      console.error('Error rendering PDF:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  const renderPage = async (pdf: any, pageNumber: number) => {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1.5 });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (context) {
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      await page.render({ canvasContext: context, viewport }).promise;
    }
    return canvas;
  };

  const footer = (<Fragment>
    <Button label="Đóng" icon="pi pi-times" outlined severity="secondary" size="small" onClick={() => setDisplay(null)} />
  </Fragment>)
  return (
    <Dialog header={'Chi tiết mẫu hợp đồng'} maximized={true} draggable={false} footer={footer} visible={!!display} style={{ width: '100%' }} position="top" onHide={() => {
      setDisplay(false);
    }}>
      <Toast ref={toast} />
      <TabView activeIndex={activeIndex} style={{ height: '100%' }} onTabChange={(e) => setActiveIndex(e.index)}>
        <TabPanel header="Thông tin">
          <div className="p-4 overflow-auto" style={{ height: 'calc(100vh - 180px)' }}>
            <Step1BasicInfo
              formData={templateData || {}}
              setFormData={() => { }}
              file={null}
              fileName={templateData?.fileName || ''}
              onFileChange={() => { }}
              documentTypeOptions={documentTypeOptions}
              readonly={true}
            />
          </div>
        </TabPanel>
        <TabPanel header="Đối tượng ký">
          <div className="p-4 overflow-auto" style={{ height: 'calc(100vh - 180px)' }}>
            <Step2Roles
              roles={roles}
              setRoles={setRoles}
              availableRoleOptions={[]}
              readonly={true}
            />
          </div>
        </TabPanel>
        <TabPanel header="Vị trí ký">
          <div className="p-2" style={{ height: 'calc(100vh - 180px)' }}>
            <Step3Positions
              roles={roles}
              selectedRole={null}
              setSelectedRole={() => { }}
              signaturePositions={signaturePositions}
              setSignaturePositions={setSignaturePositions}
              pdfLoading={pdfLoading}
              canvases={canvases}
              canvasRefs={canvasRefs}
              draggableRefs={draggableRefs}
              handleDrop={() => { }}
              handleDragStop={() => { }}
              readonly={true}
            />
          </div>
        </TabPanel>
        <TabPanel header="Trường thông tin">
          <div className="p-4 overflow-auto" style={{ height: 'calc(100vh - 180px)' }}>
            <Step4Fields
              dbFormFields={dbFormFields}
              setDbFormFields={setDbFormFields}
              formFieldTypes={formFieldTypes}
              roles={roles}
              mapProperties={mapProperties}
              readonly={true}
            />
          </div>
        </TabPanel>
        <TabPanel header="Mẫu hợp đồng">
          <div className="flex h-full overflow-hidden" style={{ height: 'calc(100vh - 180px)' }}>
            {/* Main Content */}
            <ScrollPanel style={{ flex: 1, height: '100%' }}>
              <div className="flex flex-column align-items-center p-3 gap-3 bg-ground">
                {pdfLoading && (
                  <div className="flex flex-column align-items-center p-5">
                    <i className="pi pi-spin pi-spinner text-4xl mb-3" />
                    <p>Đang tải tài liệu...</p>
                  </div>
                )}
                {!pdfLoading && canvases.map((canvas, index) => (
                  <div key={index} id={`page-preview-${index}`} className="shadow-2 bg-white mb-3">
                    <canvas ref={ref => {
                      if (ref && canvas) {
                        const context = ref.getContext('2d');
                        if (context) {
                          ref.width = canvas.width;
                          ref.height = canvas.height;
                          context.drawImage(canvas, 0, 0);
                        }
                      }
                    }} />
                  </div>
                ))}
                {!pdfLoading && canvases.length === 0 && (
                  <p className="text-sm text-600 m-0 p-4">Không có file để hiển thị hoặc đang tải...</p>
                )}
              </div>
            </ScrollPanel>

            {/* Sidebar Navigation */}
            {!pdfLoading && canvases.length > 0 && (
              <div className="sidebar-nav surface-border border-left-1 flex flex-column py-3 px-2 surface-card shadow-1" style={{ width: '220px' }}>
                <b className="text-xs text-center mb-3">SỐ TRANG</b>
                <ScrollPanel style={{ flex: 1 }} className="custom-scroll">
                  <div className="flex flex-column gap-3">
                    {canvases.map((canvas, index) => (
                      <div
                        key={index}
                        className="cursor-pointer p-1 hover:bg-blue-50 transition-all border-1 border-transparent hover:border-blue-400"
                        onClick={() => {
                          const element = document.getElementById(`page-preview-${index}`);
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }}
                      >
                        <div className="shadow-2 border-round overflow-hidden mb-1">
                          <img
                            src={canvas.toDataURL('image/png')}
                            alt={`Trang ${index + 1}`}
                            style={{ width: '100%', height: 'auto', display: 'block' }}
                          />
                        </div>
                        <div className="text-center text-xs font-semibold text-600">{index + 1}</div>
                      </div>
                    ))}
                  </div>
                </ScrollPanel>
              </div>
            )}
          </div>
        </TabPanel>
      </TabView>
    </Dialog>
  )
}

